'use client';

import { useEffect } from 'react';
import { useUser } from '@/contexts/user-context';
import { useTrackingEvents } from '@/hooks/use-tracking-events';

const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;
const SECONDS_PER_MINUTE = 60;
const MILLISECONDS_PER_SECOND = 1000;
const ONE_DAY_MS = HOURS_PER_DAY * MINUTES_PER_HOUR * SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND;
const ACTIVATION_KEY = 'trial-activation-tracked';

type TrialGoal = {
  goal?: string;
  startedAt?: string;
};

export function TrialActivationTracker() {
  const { isLoggedIn } = useUser();
  const tracking = useTrackingEvents();

  useEffect(() => {
    if (!(isLoggedIn && typeof window !== 'undefined')) {
      return;
    }
    if (window.localStorage.getItem(ACTIVATION_KEY)) {
      return;
    }

    try {
      const rawGoal = window.localStorage.getItem('trial-active-goal');
      const savedAt = window.localStorage.getItem('first-insight-saved-at');
      if (!(rawGoal && savedAt)) {
        return;
      }
      const trial = JSON.parse(rawGoal) as TrialGoal;
      const startedAt = trial.startedAt ? new Date(trial.startedAt).getTime() : Number.NaN;
      const insightSavedAt = new Date(savedAt).getTime();
      const referenceTime = Math.max(startedAt, insightSavedAt);
      if (!Number.isFinite(referenceTime) || Date.now() - referenceTime < ONE_DAY_MS) {
        return;
      }

      tracking.trackTrialActivated({
        goal: trial.goal ?? null,
        activation_rule: 'insight_saved_and_returned_next_day',
        days_since_start: Math.floor((Date.now() - referenceTime) / ONE_DAY_MS),
      });
      window.localStorage.setItem(ACTIVATION_KEY, new Date().toISOString());
    } catch {
      window.localStorage.removeItem('trial-active-goal');
    }
  }, [isLoggedIn, tracking]);

  return null;
}
